import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import {
    useDebugStatus,
    useCacheStats,
    useFormTypes,
    useFormConfig,
    useUserDirectory,
    useUserDetail,
    useUserPhoto,
    useManagerChain,
    useSimulateApprovalFlow
} from '../../../hooks/useApi';

/**
 * Dev Panel (Refactored)
 * Features:
 * 1. System Status Overview
 * 2. Simulate Approval - Like creating a form normally, but can specify user
 * 3. Manager Chain Query - Input user to query their full profile
 * 4. Operation Logs
 * 
 * Access Control: user.admin=true or user.roles includes 'admin'
 */
const DevPanel = () => {
    const { user } = useAuth();
    const { t, getText, language } = useI18n(['common', 'admin']);
    const [activeTab, setActiveTab] = useState('overview');
    const [logMessages, setLogMessages] = useState([]);
    const [showApiEndpoints, setShowApiEndpoints] = useState(false);

    // Simulate Approval
    const [selectedFormType, setSelectedFormType] = useState('');
    const [simulateFormData, setSimulateFormData] = useState({});
    const [simulateUser, setSimulateUser] = useState(null);
    const [simulationResult, setSimulationResult] = useState(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');

    // Manager Chain Query
    const [chainSearchTerm, setChainSearchTerm] = useState('');
    const [selectedChainUser, setSelectedChainUser] = useState(null);

    // Access Control
    const isAdmin = user?.admin || user?.roles?.includes('admin');

    // React Query Hooks
    const { data: statusResult, refetch: refetchStatus } = useDebugStatus();
    const { data: cacheResult } = useCacheStats();
    const { data: formTypesResult } = useFormTypes(language);
    const { data: directoryResult } = useUserDirectory();
    const { data: formConfigResult, isLoading: loadingFormConfig } = useFormConfig(selectedFormType, language);
    const { data: chainUserProfileResult } = useUserDetail(selectedChainUser?.id);
    const { data: chainUserManagersResult, isLoading: loadingChainManagers } = useManagerChain(selectedChainUser?.id, 5);
    
    const simulateMutation = useSimulateApprovalFlow();

    // Extract Data
    const systemStatus = statusResult?.data;
    const cacheStats = cacheResult?.data;
    const formTypes = formTypesResult?.data || [];
    const userDirectory = directoryResult?.data?.users || [];
    const formConfig = formConfigResult?.data;
    const chainUserProfile = chainUserProfileResult?.data;
    const chainUserManagers = chainUserManagersResult?.data?.managerChain || [];

    const loading = loadingFormConfig || simulateMutation.isPending;

    // Add Log
    const addLog = useCallback((type, message) => {
        const timestamp = new Date().toLocaleTimeString('zh-TW');
        setLogMessages(prev => [...prev.slice(-100), { type, message, timestamp }]);
    }, []);

    // Initialize Form Data
    const initializeFormData = useCallback((config) => {
        const data = {};
        // New structure: fields is an object, key is fieldId
        if (config?.fields) {
            Object.entries(config.fields).forEach(([fieldId, field]) => {
                if (field.defaultValue !== undefined) {
                    data[fieldId] = field.defaultValue === 'today' 
                        ? new Date().toISOString().split('T')[0] 
                        : field.defaultValue;
                } else if (field.type === 'number' || field.type === 'currency') {
                    data[fieldId] = 0;
                } else if (field.type === 'date') {
                    data[fieldId] = new Date().toISOString().split('T')[0];
                } else {
                    data[fieldId] = '';
                }
            });
        }
        setSimulateFormData(data);
    }, []);

    // Initialize form data when form configuration is loaded
    useEffect(() => {
        if (formConfig?.form) {
            initializeFormData(formConfig.form);
        }
    }, [formConfig, initializeFormData]);

    // Simulate Approval Flow
    const simulateApprovalFlow = async () => {
        if (!selectedFormType || !simulateUser) {
            addLog('error', 'Please select a form type and applicant');
            return;
        }

        try {
            addLog('info', `Start simulating approval: ${selectedFormType} - Applicant: ${simulateUser.displayName}`);

            const result = await simulateMutation.mutateAsync({
                formType: selectedFormType,
                formData: simulateFormData,
                applicant: {
                    userId: simulateUser.id,
                    displayName: simulateUser.displayName,
                    email: simulateUser.mail,
                    department: simulateUser.department,
                    jobTitle: simulateUser.jobTitle
                },
                amount: simulateFormData.total_estimated_cost || simulateFormData.total_expense || 0
            });

            if (result?.data) {
                setSimulationResult(result.data);
                addLog('success', 'Simulation completed successfully');
            } else {
                addLog('error', 'Simulation failed: ' + (result?.message || 'Unknown error'));
            }
        } catch (err) {
            addLog('error', 'Simulation error: ' + err.message);
        }
    };

    // Search Manager Chain Users (using useMemo)
    const chainSearchResults = useMemo(() => {
        if (!chainSearchTerm || chainSearchTerm.length < 2) {
            return [];
        }
        
        return userDirectory.filter(u => {
            const lowerTerm = chainSearchTerm.toLowerCase();
            return (
                u.displayName?.toLowerCase().includes(lowerTerm) ||
                u.mail?.toLowerCase().includes(lowerTerm)
            );
        }).slice(0, 10);
    }, [chainSearchTerm, userDirectory]);

    // Select User to Query Manager Chain
    const loadUserManagerChain = (userData) => {
        setSelectedChainUser(userData);
        addLog('info', `Query Manager Chain: ${userData.displayName}`);
    };

    // Log when Manager Chain is loaded
    useEffect(() => {
        if (selectedChainUser && chainUserManagers.length > 0) {
            addLog('success', `Found ${chainUserManagers.length} levels of Managers for ${selectedChainUser.displayName}`);
        }
    }, [chainUserManagers, selectedChainUser, addLog]);

    // Permission Check
    if (!isAdmin) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <h2 className="text-xl font-bold text-red-800 mb-2">🔒 Access Denied</h2>
                    <p className="text-red-600">You need admin privileges to access the debug panel.</p>
                    <p className="text-sm text-gray-500 mt-2">
                        Requires user.admin=true or user.roles includes 'admin'
                    </p>
                </div>
            </div>
        );
    }

    // Tab Buttons
    const TabButton = ({ id, label, icon }) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === id
                    ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
        >
            {icon} {label}
        </button>
    );

    // JSON Viewer
    const JsonViewer = ({ data, title, maxHeight = '400px' }) => (
        <div className="mb-4">
            {title && <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>}
            <pre 
                className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-auto"
                style={{ maxHeight }}
            >
                {JSON.stringify(data, null, 2)}
            </pre>
        </div>
    );

    // Manager Chain Visualization
    const ManagerChainViewer = ({ chain }) => {
        if (!chain || chain.length === 0) {
            return <p className="text-gray-500 italic">No Manager Chain data</p>;
        }

        return (
            <div className="space-y-3 ml-4">
                {chain.map((manager, index) => (
                    <div key={manager.userId || index} className="relative">
                        {/* Connector Line */}
                        {index < chain.length - 1 && (
                            <div className="absolute left-4 top-12 w-0.5 h-6 bg-blue-300"></div>
                        )}
                        
                        {/* Node */}
                        <div className="flex items-start">
                            <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                                {manager.level || index + 1}
                            </div>
                            <div className="flex-1 bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <p className="font-medium text-gray-900">{manager.displayName}</p>
                                <p className="text-sm text-gray-600">{manager.jobTitle}</p>
                                <p className="text-xs text-gray-500">{manager.email} | {manager.department}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Filter Users
    const filteredUsers = userDirectory.filter(u => {
        if (!userSearchTerm) return true;
        const term = userSearchTerm.toLowerCase();
        return (
            u.displayName?.toLowerCase().includes(term) ||
            u.mail?.toLowerCase().includes(term)
        );
    }).slice(0, 20);

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Title */}
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <span className="mr-2">🛠️</span>
                        Developer Debug Panel
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Current User: <span className="font-medium">{user?.displayName}</span>
                    </p>
                </div>
                <div className="text-right text-sm">
                    <p className="text-orange-600 font-medium">⚠️ Development Only</p>
                    <p className="text-gray-500">{new Date().toLocaleString('zh-TW')}</p>
                </div>
            </div>

            {/* Quick Links - Organized Navigation */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <Link 
                    to="/config" 
                    className="flex flex-col items-center p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">⚙️</span>
                    <span className="text-sm font-medium text-purple-800">System Config</span>
                </Link>
                <Link 
                    to="/dev/notifications"
                    className="flex flex-col items-center p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">🔧</span>
                    <span className="text-sm font-medium text-blue-800">Notification Tester</span>
                </Link>
                <a 
                    href="/api/debug/status" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
                >
                    <span className="text-2xl mb-1">📡</span>
                    <span className="text-sm font-medium text-gray-800">API Status</span>
                </a>
            </div>

            {/* API Endpoints Quick Reference */}
            <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700">📡 Common API Endpoints</h3>
                    <button 
                        onClick={() => setShowApiEndpoints(!showApiEndpoints)}
                        className="text-xs text-blue-600 hover:underline"
                    >
                        {showApiEndpoints ? 'Hide' : 'Show'}
                    </button>
                </div>
                {showApiEndpoints && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs font-mono">
                        <div className="space-y-1">
                            <p className="font-semibold text-gray-600">📋 Forms</p>
                            <a href="/api/forms/types" target="_blank" className="block text-blue-600 hover:underline">GET /api/forms/types</a>
                            <a href="/api/forms/list" target="_blank" className="block text-blue-600 hover:underline">GET /api/forms/list</a>
                            <span className="block text-gray-500">GET /api/forms/:id</span>
                            <span className="block text-gray-500">POST /api/forms/create</span>
                        </div>
                        <div className="space-y-1">
                            <p className="font-semibold text-gray-600">👥 Users</p>
                            <a href="/api/users/directory" target="_blank" className="block text-blue-600 hover:underline">GET /api/users/directory</a>
                            <span className="block text-gray-500">GET /api/users/:id</span>
                            <span className="block text-gray-500">GET /api/users/:id/manager-chain</span>
                            <span className="block text-gray-500">GET /api/users/:id/photo</span>
                        </div>
                        <div className="space-y-1">
                            <p className="font-semibold text-gray-600">🔧 Debug</p>
                            <a href="/api/debug/status" target="_blank" className="block text-blue-600 hover:underline">GET /api/debug/status</a>
                            <a href="/api/debug/cache" target="_blank" className="block text-blue-600 hover:underline">GET /api/debug/cache</a>
                            <a href="/api/i18n/en" target="_blank" className="block text-blue-600 hover:underline">GET /api/i18n/:locale</a>
                        </div>
                    </div>
                )}
            </div>

            {/* Tab Buttons */}
            <div className="flex space-x-1 mb-0 border-b border-gray-200">
                <TabButton id="overview" label="System Status" icon="📊" />
                <TabButton id="simulate" label="Simulate Approval" icon="🎮" />
                <TabButton id="chain" label="Manager Chain" icon="🔗" />
                <TabButton id="logs" label="Operation Logs" icon="📋" />
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-b-lg rounded-tr-lg shadow-md p-6 border border-gray-200 border-t-0">
                
                {/* System Status */}
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">🖥️ System Status</h3>
                            {systemStatus ? (
                                <div className="space-y-2 text-sm">
                                    <p>Environment: <span className="font-medium">{systemStatus.environment}</span></p>
                                    <p>Node: <span className="font-medium">{systemStatus.nodeVersion}</span></p>
                                    <p>Uptime: <span className="font-medium">{Math.floor(systemStatus.uptime / 60)} minutes</span></p>
                                </div>
                            ) : (
                                <p className="text-gray-500">Loading...</p>
                            )}
                        </div>

                        <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                            <h3 className="text-lg font-semibold text-green-900 mb-2">📦 Cache Status</h3>
                            {cacheStats ? (
                                <div className="space-y-2 text-sm">
                                    <p>User Directory: <span className="font-medium">{cacheStats.directory?.userCount || 0} people</span></p>
                                    <p>Directory TTL: <span className="font-medium">{cacheStats.directory?.ttl || 0} seconds</span></p>
                                    <p>User Data TTL: <span className="font-medium">{cacheStats.config?.USER_DATA || 0} seconds</span></p>
                                </div>
                            ) : (
                                <p className="text-gray-500">Loading...</p>
                            )}
                            <button
                                onClick={() => refetchStatus()}
                                className="mt-4 text-sm text-green-600 hover:underline"
                            >
                                🔄 Reload
                            </button>
                        </div>

                        <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                            <h3 className="text-lg font-semibold text-purple-900 mb-2">📝 Form Types</h3>
                            <p className="text-3xl font-bold text-purple-600">{formTypes.length}</p>
                            <div className="mt-2 space-y-1">
                                {formTypes.slice(0, 3).map(ft => (
                                    <p key={ft.id} className="text-sm text-purple-700">{getText(ft.name)}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Simulate Approval */}
                {activeTab === 'simulate' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: Form Settings */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900">Simulate Approval Process</h3>

                            {/* Select Form Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Form Type</label>
                                <select
                                    value={selectedFormType}
                                    onChange={(e) => setSelectedFormType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Select Form Type...</option>
                                    {formTypes.map(ft => (
                                        <option key={ft.id} value={ft.id}>{getText(ft.name)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Select Applicant */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Applicant</label>
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                                />
                                {simulateUser ? (
                                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-200">
                                        <div>
                                            <p className="font-medium">{simulateUser.displayName}</p>
                                            <p className="text-sm text-gray-500">{simulateUser.mail}</p>
                                        </div>
                                        <button
                                            onClick={() => setSimulateUser(null)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="max-h-40 overflow-auto border border-gray-200 rounded-lg">
                                        {filteredUsers.map(u => (
                                            <div
                                                key={u.id}
                                                onClick={() => {
                                                    setSimulateUser(u);
                                                    setUserSearchTerm('');
                                                }}
                                                className="p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                                            >
                                                <p className="font-medium text-sm">{u.displayName}</p>
                                                <p className="text-xs text-gray-500">{u.mail}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Key Fields */}
                            {formConfig && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Fields</label>
                                    <div className="space-y-2">
                                        {/* Filter key fields from formConfig.form.fields object */}
                                        {Object.entries(formConfig.form?.fields || {})
                                            .filter(([fieldId, field]) => 
                                                fieldId.includes('cost') || 
                                                fieldId.includes('expense') || 
                                                fieldId.includes('amount') ||
                                                fieldId.includes('date') ||
                                                fieldId.includes('type')
                                            )
                                            .map(([fieldId, field]) => (
                                                <div key={fieldId}>
                                                    <label className="text-xs text-gray-600">{field.label || fieldId}</label>
                                                    <input
                                                        type={field.type === 'date' ? 'date' : field.type === 'number' || field.type === 'currency' ? 'number' : 'text'}
                                                        value={simulateFormData[fieldId] || ''}
                                                        onChange={(e) => setSimulateFormData(prev => ({
                                                            ...prev,
                                                            [fieldId]: field.type === 'number' || field.type === 'currency' 
                                                                ? parseFloat(e.target.value) || 0 
                                                                : e.target.value
                                                        }))}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}

                            {/* 執行模擬 */}
                            <button
                                onClick={simulateApprovalFlow}
                                disabled={loading || !selectedFormType || !simulateUser}
                                className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {loading ? 'Simulating...' : '🎮 Run Simulation'}
                            </button>
                        </div>

                        {/* Right: Simulation Results */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Simulation Results</h3>
                            
                            {simulationResult ? (
                                <div className="space-y-4">
                                    {/* Approval Flow Visualization */}
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-700 mb-3">Approval Flow</h4>
                                        <div className="space-y-2">
                                            {simulationResult.approvalFlow?.map((step, idx) => (
                                                <div key={idx} className="flex items-center">
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-2 ${
                                                        step.status === 'completed' ? 'bg-green-500 text-white' :
                                                        step.status === 'current' ? 'bg-blue-500 text-white' :
                                                        'bg-gray-300 text-gray-600'
                                                    }`}>
                                                        {idx + 1}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium">{getText(step.stepName)}</p>
                                                        <p className="text-xs text-gray-500">{step.approver?.displayName || step.roleType}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Manager Chain */}
                                    {simulationResult.managerChain && (
                                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                            <h4 className="font-medium text-blue-700 mb-3">Manager Chain</h4>
                                            <ManagerChainViewer chain={simulationResult.managerChain} />
                                        </div>
                                    )}

                                    {/* Complete JSON */}
                                    <JsonViewer 
                                        data={simulationResult} 
                                        title="Complete Simulation Result JSON (Format Stored in Database)" 
                                        maxHeight="300px"
                                    />
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-4xl mb-2">🎮</p>
                                    <p>Set parameters and run simulation</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Manager Chain Query */}
                {activeTab === 'chain' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: Search */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Query User Manager Chain</h3>
                            
                            <div className="mb-4">
                                <input
                                    type="text"
                                    placeholder="Search users (name or email)..."
                                    value={chainSearchTerm}
                                    onChange={(e) => setChainSearchTerm(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Search Results */}
                            {chainSearchResults.length > 0 && (
                                <div className="space-y-2 mb-4">
                                    {chainSearchResults.map(u => (
                                        <div
                                            key={u.id}
                                            onClick={() => loadUserManagerChain(u)}
                                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                selectedChainUser?.id === u.id
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <p className="font-medium">{u.displayName}</p>
                                            <p className="text-sm text-gray-500">{u.mail}</p>
                                            <p className="text-xs text-gray-400">{u.jobTitle} | {u.department}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {chainSearchTerm && chainSearchResults.length === 0 && (
                                <p className="text-gray-500 text-center py-4">No matching users found</p>
                            )}

                            {!chainSearchTerm && (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-4xl mb-2">🔍</p>
                                    <p>Enter name or Email to search users</p>
                                </div>
                            )}
                        </div>

                        {/* Right: Results */}
                        <div>
                            {selectedChainUser ? (
                                <div className="space-y-4">
                                    {/* User Profile */}
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h4 className="font-medium text-gray-700 mb-3">User Information</h4>
                                        <div className="flex items-center mb-3">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold mr-3">
                                                {selectedChainUser.displayName?.charAt(0) || '?'}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-lg">{selectedChainUser.displayName}</p>
                                                <p className="text-gray-500">{selectedChainUser.mail}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <span className="text-gray-500">Job Title:</span>
                                                <span className="ml-1">{chainUserProfile?.jobTitle || selectedChainUser.jobTitle || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Department:</span>
                                                <span className="ml-1">{chainUserProfile?.department || selectedChainUser.department || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Company:</span>
                                                <span className="ml-1">{chainUserProfile?.companyName || '-'}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Location:</span>
                                                <span className="ml-1">{chainUserProfile?.officeLocation || '-'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Manager Chain */}
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-medium text-blue-700">Manager Chain</h4>
                                            <span className="text-sm text-blue-600">{chainUserManagers.length} Layers</span>
                                        </div>
                                        {loadingChainManagers ? (
                                            <div className="text-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                            </div>
                                        ) : (
                                            <ManagerChainViewer chain={chainUserManagers} />
                                        )}
                                    </div>

                                    {/* JSON 資料 */}
                                    <JsonViewer 
                                        data={{
                                            user: chainUserProfile || selectedChainUser,
                                            managerChain: chainUserManagers
                                        }} 
                                        title="Complete JSON Data" 
                                        maxHeight="200px"
                                    />
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-4xl mb-2">👤</p>
                                    <p>Select a user to view Manager Chain</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 操作日誌 */}
                {activeTab === 'logs' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">操作日誌</h3>
                            <button
                                onClick={() => setLogMessages([])}
                                className="text-sm text-red-600 hover:underline"
                            >
                                清除日誌
                            </button>
                        </div>

                        <div className="bg-gray-900 rounded-lg p-4 max-h-[60vh] overflow-auto font-mono text-sm">
                            {logMessages.length === 0 ? (
                                <p className="text-gray-500">暫無日誌</p>
                            ) : (
                                logMessages.map((log, index) => (
                                    <div
                                        key={index}
                                        className={`py-1 ${
                                            log.type === 'error' ? 'text-red-400' :
                                            log.type === 'success' ? 'text-green-400' :
                                            log.type === 'warning' ? 'text-yellow-400' :
                                            'text-gray-300'
                                        }`}
                                    >
                                        <span className="text-gray-500">[{log.timestamp}]</span>{' '}
                                        <span className={`font-medium ${
                                            log.type === 'error' ? 'text-red-500' :
                                            log.type === 'success' ? 'text-green-500' :
                                            log.type === 'warning' ? 'text-yellow-500' :
                                            'text-blue-500'
                                        }`}>[{log.type.toUpperCase()}]</span>{' '}
                                        {log.message}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevPanel;
